/**
 * OSAPIENS-compatible export helpers.
 *
 * OSAPIENS is an independent third-party EUDR system. These pure functions build a
 * SEPARATE, OSAPIENS-shaped GeoJSON FeatureCollection from the same production-place
 * data the DDS Final Report already holds. They never mutate the source data and never
 * invent missing values — unresolved required fields are reported for the user to decide
 * (see validateForOsapiens).
 *
 * Dimitra's official EUDR GeoJSON builders are untouched; this is a compatibility export.
 *
 * Mirrors the field logic of PublicDDSReport.vue's downloadGeoJSON(), re-expressed with
 * OSAPIENS property names + commodity enum + unique plot ids.
 */
import { countries } from '@/constants/countries.js';
import { countryNamesMatch } from '@/constants/countryAliases.js';
import { acreToHectare } from '@/_services/UnitService.js';
import { rewindGeoJson, ensureClosedPolygonRing } from '@/geo-utils.js';

/** Pinned ruleset version — recorded in the export audit trail. Bump when rules change. */
export const OSAPIENS_RULE_VERSION = '2026-10';

/** OSAPIENS eudr_commodity enum. */
export const OSAPIENS_COMMODITIES = ['WOOD', 'RUBBER', 'PALM_OIL', 'SOY', 'CATTLE', 'COFFEE', 'COCOA'];

/** Transformation categories the user must authorize (keys for i18n + audit). */
export const OSAPIENS_CATEGORIES = {
  FIELD_RENAME: 'field-rename',
  DUPLICATE_NAMES: 'duplicate-names',
  COUNTRY_ISO: 'country-iso',
  COMMODITY_ENUM: 'commodity-enum',
};

/** Acre footprint Dimitra assigns to a point when no real area is known (~4 ha). */
const DEFAULT_POINT_AREA_ACRES = 9.8842;

/** Dimitra product name fragment -> OSAPIENS enum. Matched by substring, lower-cased. */
const COMMODITY_MAP = {
  cocoa: 'COCOA',
  cacao: 'COCOA',
  coffee: 'COFFEE',
  'palm oil': 'PALM_OIL',
  palm: 'PALM_OIL',
  rubber: 'RUBBER',
  soybean: 'SOY',
  soya: 'SOY',
  soy: 'SOY',
  cattle: 'CATTLE',
  beef: 'CATTLE',
  timber: 'WOOD',
  wood: 'WOOD',
};

/** Match the public builder's rounding exactly (PublicDDSReport.roundUpToXixDecimal). */
function round6(input) {
  return Math.ceil(Number(input) * 1e6) / 1e6;
}

/**
 * Resolve a free-text country (name or ISO code) to an ISO2 code, or '' if unresolved.
 * Never guesses — an unresolved value is surfaced as missing info, not fabricated.
 */
export function resolveCountryCode(value) {
  if (!value && value !== 0) return '';
  const v = String(value).trim();
  if (!v) return '';
  const byCode = countries.find((c) => c.Code.toLowerCase() === v.toLowerCase());
  if (byCode) return byCode.Code;
  const byName = countries.find((c) => countryNamesMatch(c.Name, v));
  return byName ? byName.Code : '';
}

/**
 * Try several already-present fields in priority order and return the first that resolves
 * to a valid ISO2 code. Each candidate is looked up strictly (resolveCountryCode never
 * guesses), so this only ever returns a code that a real, available field named.
 */
export function resolveCountryCodeFromCandidates(candidates) {
  if (!Array.isArray(candidates)) return '';
  for (let i = 0; i < candidates.length; i += 1) {
    const code = resolveCountryCode(candidates[i]);
    if (code) return code;
  }
  return '';
}

/**
 * Country values available for a plot, most specific first:
 *   per-plot producer country, per-plot farm country, then the DD's country of production
 *   ONLY when it names a single, unambiguous country. A multi-country DD does not fall back
 *   (we prompt instead rather than stamp the wrong country on a plot).
 */
function countrySources(item, report = {}) {
  const farm = (item && item.farm) || {};
  const assoc = farm.userDdsAssoc || {};
  const cop = report.countryOfProduction;
  let ddCountry = null;
  if (Array.isArray(cop)) {
    if (cop.length === 1) ddCountry = cop[0];
  } else if (cop) {
    ddCountry = cop;
  }
  return [assoc.countryId, farm.country, assoc.country, ddCountry];
}

/** Map a Dimitra product/commodity string to the OSAPIENS enum, or '' if unmappable. */
export function resolveCommodity(value) {
  if (!value) return '';
  const v = String(value).trim();
  if (!v) return '';
  const upper = v.toUpperCase();
  if (OSAPIENS_COMMODITIES.includes(upper)) return upper;
  const lower = v.toLowerCase();
  const hit = Object.keys(COMMODITY_MAP).find((frag) => lower.includes(frag));
  return hit ? COMMODITY_MAP[hit] : '';
}

/** Dedupe interior vertices while keeping first/last (mirrors public builder). Expects [lng,lat]. */
function dedupeRing(coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length < 3) return coordinates || [];
  const out = [coordinates[0]];
  const seen = new Set([`${coordinates[0][0]},${coordinates[0][1]}`]);
  for (let i = 1; i < coordinates.length - 1; i += 1) {
    const key = `${coordinates[i][0]},${coordinates[i][1]}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(coordinates[i]);
    }
  }
  out.push(coordinates[coordinates.length - 1]);
  return out;
}

/**
 * Unique plot_id from a base name. First occurrence keeps the name; later duplicates get a
 * ` - NNN` suffix ONLY when the user accepted the duplicate-names transformation.
 */
function uniquePlotId(base, used, dedupeOn) {
  const count = (used.get(base) || 0) + 1;
  used.set(base, count);
  if (count === 1 || !dedupeOn) return base;
  return `${base} - ${String(count).padStart(3, '0')}`;
}

const isPointType = (item) => String(item && item.farmType).toLowerCase() === 'point';

/**
 * Inspect production places and report which transformations apply and what required info is
 * missing — WITHOUT building the file. The dialog uses this to request per-category consent
 * and to prompt for missing data (§4/§5). Pure, read-only.
 *
 * @returns {{categories: string[], missing: Array, duplicates: string[]}}
 */
export function validateForOsapiens(productionPlaces, report = {}) {
  const places = Array.isArray(productionPlaces) ? productionPlaces : [];
  const categories = new Set();
  const missing = [];
  const nameCounts = {};
  const reportCommodity = resolveCommodity(report.product || report.productCommonName);

  places.forEach((item, index) => {
    const farm = (item && item.farm) || {};
    const plot = (farm.farmName || '').trim();
    categories.add(OSAPIENS_CATEGORIES.FIELD_RENAME); // rename + acre->ha always applies

    if (plot) {
      const key = plot.toLowerCase();
      nameCounts[key] = (nameCounts[key] || 0) + 1;
    }

    const sources = countrySources(item, report);
    if (resolveCountryCodeFromCandidates(sources)) {
      categories.add(OSAPIENS_CATEGORIES.COUNTRY_ISO);
    } else {
      const hadAnyCountry = sources.some((s) => s != null && String(s).trim() !== '');
      missing.push({ index, plot, field: 'country_code', reason: hadAnyCountry ? 'countryUnresolved' : 'countryMissing' });
    }

    const commodity = resolveCommodity(item && item.commodity) || reportCommodity;
    if (commodity) {
      categories.add(OSAPIENS_CATEGORIES.COMMODITY_ENUM);
    } else {
      missing.push({ index, plot, field: 'eudr_commodity', reason: 'commodityUnmapped' });
    }

    if (commodity === 'WOOD' && !((report.productScientificName || '').trim())) {
      missing.push({ index, plot, field: 'scientific_products', reason: 'speciesMissing' });
    }

    if (isPointType(item)) {
      if (!Number.isFinite(Number(farm.lat)) || !Number.isFinite(Number(farm.log))) {
        missing.push({ index, plot, field: 'geometry', reason: 'pointMissing' });
      }
    } else if (!(Array.isArray(farm.FarmCoordinates) && farm.FarmCoordinates.length >= 3)) {
      missing.push({ index, plot, field: 'geometry', reason: 'polygonInvalid' });
    }
  });

  const duplicates = Object.keys(nameCounts).filter((k) => nameCounts[k] > 1);
  if (duplicates.length) categories.add(OSAPIENS_CATEGORIES.DUPLICATE_NAMES);

  return { categories: Array.from(categories), missing, duplicates };
}

/**
 * Build the OSAPIENS-compatible FeatureCollection.
 *
 * @param {Array}  productionPlaces  FinalReport `productionPlaces` items.
 * @param {Object} report            FinalReport `report` (for product / scientific name).
 * @param {Object} options
 * @param {string[]} options.acceptedCategories  Categories the user authorized.
 * @param {Object}   options.resolutions         Per-index overrides for missing info,
 *                                                keyed by place index:
 *                                                { commodity, countryCode, scientificProducts }.
 */
export function buildOsapiensGeoJson(productionPlaces, report = {}, options = {}) {
  const places = Array.isArray(productionPlaces) ? productionPlaces : [];
  const accepted = new Set(options.acceptedCategories || []);
  const resolutions = options.resolutions || {};
  const dedupeNames = accepted.has(OSAPIENS_CATEGORIES.DUPLICATE_NAMES);
  const reportCommodity = resolveCommodity(report.product || report.productCommonName);
  const reportScientific = (report.productScientificName || '').trim();

  const geojson = { type: 'FeatureCollection', features: [] };
  const usedPlotIds = new Map();

  places.forEach((item, index) => {
    const farm = (item && item.farm) || {};
    const res = resolutions[index] || {};
    const feature = { type: 'Feature', properties: {}, geometry: {} };

    const baseName = (farm.farmName || '').trim();
    if (baseName) {
      feature.properties.plot_id = uniquePlotId(baseName, usedPlotIds, dedupeNames);
    }

    const countryCode = (res.countryCode && res.countryCode.trim())
      || resolveCountryCodeFromCandidates(countrySources(item, report));
    if (countryCode) feature.properties.country_code = countryCode;

    const commodity = resolveCommodity(item && item.commodity) || res.commodity || reportCommodity;
    if (commodity) feature.properties.eudr_commodity = commodity;

    if (commodity === 'WOOD') {
      const species = res.scientificProducts || reportScientific;
      if (species) feature.properties.scientific_products = species;
    }

    if (isPointType(item)) {
      feature.geometry.type = 'Point';
      feature.geometry.coordinates = [round6(farm.log), round6(farm.lat)];
      const areaAcres = Number(farm.area);
      if (Number.isFinite(areaAcres) && areaAcres > 0 && areaAcres !== DEFAULT_POINT_AREA_ACRES) {
        feature.properties['Area (ha)'] = acreToHectare(areaAcres);
      }
    } else {
      feature.geometry.type = 'Polygon';
      const ring = (farm.FarmCoordinates || []).map((c) => [round6(c.log), round6(c.lat)]);
      const unique = dedupeRing(ring);
      ensureClosedPolygonRing(unique);
      feature.geometry.coordinates = [unique];
      // Area omitted for polygons — OSAPIENS derives it from the boundary.
    }

    geojson.features.push(feature);
  });

  rewindGeoJson(geojson);
  return geojson;
}
